import React, { useState, useEffect } from "react";
import {
    Building2,
    Globe,
    MapPin,
    Calendar,
    Users,
    TrendingUp,
    Clock,
    CheckCircle2,
    AlertCircle,
    Briefcase
} from "lucide-react";
import { useParams } from "react-router-dom";
import { MAIN_URL } from "../../../Configurations/Urls";

const formatMinutes = (mins) => {
    if (!mins || mins === 0) return "0h 00m";

    const hours = Math.floor(mins / 60);
    const minutes = Math.round(mins % 60);

    return `${hours}h ${minutes.toString().padStart(2, "0")}m`;
};


const formatHours = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return "0h 00m";

    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    // Handle floating point imprecision (like 59.999 → 60)
    const finalMinutes = minutes === 60 ? 0 : minutes;
    const finalHours = minutes === 60 ? hours + 1 : hours;

    return `${finalHours}h ${finalMinutes.toString().padStart(2, "0")}m`;
};


const ClientDetailsDashboard = () => {
    const [clientData, setClientData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedProject, setExpandedProject] = useState(null);

    const { id } = useParams();

    useEffect(() => {
        fetchClientData();
    }, [id]);

    const fetchClientData = async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${MAIN_URL}/api/project-management/clients/view/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );

            const result = await response.json();

            if (result.success) {
                setClientData(result.data);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError("Failed to fetch client data");
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            Active: "bg-green-100 text-green-800",
            Completed: "bg-blue-100 text-blue-800",
            Pending: "bg-yellow-100 text-yellow-800",
            "In Progress": "bg-orange-100 text-orange-800",
            Cancelled: "bg-red-100 text-red-800",
        };
        return colors[status] || "bg-gray-100 text-gray-800";
    };

    const getPriorityColor = (priority) => {
        const colors = {
            High: "text-red-600",
            Medium: "text-orange-600",
            Low: "text-green-600",
        };
        return colors[priority] || "text-gray-600";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading client details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <p className="text-red-800 text-center">{error}</p>
                </div>
            </div>
        );
    }

    const { client, projects } = clientData;

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">

                {/* HEADER */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <div className="flex items-start space-x-4">
                        <div className="bg-blue-600 text-white rounded-lg p-4">
                            <Building2 className="w-8 h-8" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">{client.name}</h1>

                            <div className="flex items-center space-x-4 mt-2">
                                <span className="text-sm text-gray-500">Short Name: {client.short_name}</span>

                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                        client.status
                                    )}`}
                                >
                                    {client.status}
                                </span>
                            </div>

                            <p className="mt-2 text-sm text-gray-600">
                                Client Since: {client.client_since}
                            </p>
                        </div>
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                        <div className="bg-blue-50 rounded-lg p-4">
                            <p className="text-sm text-blue-600 font-medium">Total Projects</p>
                            <p className="text-2xl font-bold text-blue-900">
                                {client.statistics.total_projects}
                            </p>
                        </div>

                        <div className="bg-green-50 rounded-lg p-4">
                            <p className="text-sm text-green-600 font-medium">Active Projects</p>
                            <p className="text-2xl font-bold text-green-900">
                                {/* {client.statistics.active_projects} */}
                                {
                                    projects?.filter(f => f.team_members.length > 0)?.length
                                }
                            </p>
                        </div>

                        {/* <div className="bg-purple-50 rounded-lg p-4">
                            <p className="text-sm text-purple-600 font-medium">Completed</p>
                            <p className="text-2xl font-bold text-purple-900">
                                {client.statistics.completed_projects}
                            </p>
                        </div> */}

                        <div className="bg-orange-50 rounded-lg p-4">
                            <p className="text-sm text-orange-600 font-medium">Total Hours Logged</p>
                            <p className="text-2xl font-bold text-orange-900">
                                {formatHours(client.statistics.total_hours_logged.toFixed(2))}
                            </p>
                        </div>
                    </div>

                    {/* LOCATION */}
                    <div className="mt-6">
                        <h2 className="text-lg font-semibold text-gray-800">Location</h2>
                        <p className="text-gray-600 mt-1">
                            {client.location.city}, {client.location.state},{" "}
                            {client.location.country}
                        </p>
                    </div>

                    {/* CONTACTS */}
                    <div className="mt-6">
                        <h2 className="text-lg font-semibold text-gray-800">Contacts</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                            {client.contacts.length === 0 ? (
                                <p className="text-gray-500">No contact persons</p>
                            ) : (
                                client.contacts.map((c) => (
                                    <div key={c.id} className="bg-gray-100 p-4 rounded-lg">
                                        <p className="font-semibold">{c.name || "-"}</p>
                                        <p className="text-sm">{c.designation}</p>
                                        <p className="text-sm">Email: {c.email || "-"}</p>
                                        <p className="text-sm">Phone: {c.phone || "-"}</p>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* PROJECT LIST */}
                <h2 className="text-2xl font-bold mb-4">Projects</h2>

                <div className="space-y-4">
                    {projects.map((project) => (
                        <div
                            key={project.project_id}
                            className="bg-white p-6 rounded-xl shadow-md"
                        >
                            <div className="flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-bold">
                                        {project.project_name}
                                    </h3>

                                    <div className="flex items-center space-x-4 mt-2">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                project.status
                                            )}`}
                                        >
                                            {project.status}
                                        </span>

                                        {project.priority && (
                                            <span
                                                className={`font-semibold ${getPriorityColor(
                                                    project.priority
                                                )}`}
                                            >
                                                {project.priority}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* PROJECT DETAILS */}
                            <div className="mt-4 text-gray-700">
                                {/* <p>
                                    <b>Start Date:</b> {project.dates.start_date}
                                </p> */}
                                <p>
                                    <b>This Month's </b>
                                </p>

                                <p className="mt-1">
                                    <b>Estimated Hours:</b> {formatHours(project.hours.estimated)}
                                </p>

                                <p className="mt-1">
                                    <b>Actual Logged:</b> {formatHours(project.hours.actual_logged.toFixed(2))}
                                </p>
                            </div>

                            {/* TEAM MEMBERS */}
                            <div className="mt-5">
                                <h4 className="font-semibold mb-2">Team Members</h4>

                                {project.team_members.length === 0 ? (
                                    <p className="text-gray-500">No team assigned</p>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {project.team_members.map((m) => (
                                            <div
                                                key={m.organization_person_id}
                                                className="border p-3 rounded-lg flex items-center space-x-3"
                                            >
                                                <img
                                                    src={m.profile_image}
                                                    className="w-12 h-12 rounded-full object-cover"
                                                    alt="employee"
                                                />

                                                <div>
                                                    <p className="font-semibold">{m.name}</p>
                                                    <p className="text-sm text-gray-600">{m.designation}</p>

                                                    <p className="text-sm">
                                                        <b>Efficiency:</b> {m.efficiency_percentage ?? "-"}%
                                                    </p>

                                                    <p className="text-sm">
                                                        <b>Tasks Assigned:</b> {m.tasks_assigned}
                                                    </p>

                                                    <p className="text-sm">
                                                        <b>Tasks Completed:</b> {m.tasks_completed}
                                                    </p>

                                                    <p className="text-sm">
                                                        <b>Total Estimated:</b> {formatMinutes(m.total_estimated_minutes)}
                                                    </p>

                                                    <p className="text-sm">
                                                        <b>Total Actual:</b> {formatMinutes(m.total_actual_minutes)}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ClientDetailsDashboard;
